<script lang="ts">
  import type { Installator } from "$lib/types/installator";
  import { onMount } from "svelte";
  import { Loader } from "@googlemaps/js-api-loader";
  import greenDot from "$lib/assets/google-maps-icons/green-dot.png";
  import redDot from "$lib/assets/google-maps-icons/red-dot.png";
  import yellowDot from "$lib/assets/google-maps-icons/yellow-dot.png";
  import InfoBanner from "$lib/InfoBanner.svelte";
  import TypeformButton from "$lib/TypeformButton.svelte";

  // eslint-disable-next-line no-undef
  const mapOptions: google.maps.MapOptions = {
    center: { lat: 63.0, lng: 10.0 },
    zoom: 4,
    fullscreenControl: false,
    mapTypeControl: false,
    streetViewControl: false,
  };

  interface Props {
    installatorer: Installator[];
    apiKey: string;
    product: string;
    productId: string;
    typeform: string;
    campaign?: string | undefined;
    source?: string | undefined;
  }

  let {
    installatorer,
    apiKey,
    product,
    productId,
    typeform,
    campaign = undefined,
    source = undefined,
  }: Props = $props();

  let selected_installer_id = $state("");
  let selected_installer = $state("");
  // eslint-disable-next-line no-undef
  let map: google.maps.Map;
  let container: HTMLDivElement;
  let inputField: HTMLInputElement;
  // eslint-disable-next-line no-undef
  let markers: google.maps.Marker[] = [];
  // eslint-disable-next-line no-undef
  let greenMarker: google.maps.Marker;
  let current_installator = $state(installatorer);
  // eslint-disable-next-line no-undef
  let autocomplete: google.maps.places.Autocomplete;

  const selectInstallator = (installator: Installator) => {
    const lat = installator.lat;
    const lng = installator.lng;
    // eslint-disable-next-line no-undef
    const installatorLocation = new google.maps.LatLng(lat, lng);

    map.setCenter(installatorLocation);
    map.setZoom(10);

    validInstallators = validInstallators.map((i) => {
      if (i.app_item_id === installator.app_item_id) {
        return { ...i, selected: true };
      }
      return { ...i, selected: false };
    });

    selected_installer_id =
      validInstallators.find((installator) => installator.selected)?.item_id.toString() || "";
    selected_installer = validInstallators.find((installator) => installator.selected)?.title || "";

    markers.map((marker) => {
      if (marker.getTitle() === installator.title) {
        marker.setIcon({ url: redDot });
      } else {
        marker.setIcon({ url: yellowDot });
      }
    });
  };

  let productKey = $derived(typeform === "gEDXubEi" ? "produkt" : "product");
  let loader = $derived(
    new Loader({
      apiKey: apiKey,
      version: "weekly",
      libraries: ["places", "geometry"],
    })
  );
  let validInstallators = $state(
    [] as (Installator & { distance: number } & { selected: boolean })[]
  );
  $effect(() => {
    validInstallators = [] as (Installator & { distance: number } & { selected: boolean })[];
  });
  $effect(() => {
    if (selected_installer_id === "") {
      current_installator = installatorer;
    }
  });

  onMount(async () => {
    await loader.load();
    // eslint-disable-next-line no-undef
    map = new google.maps.Map(container, mapOptions);
    // eslint-disable-next-line no-undef
    const service = new google.maps.DistanceMatrixService();
    // eslint-disable-next-line no-undef
    const defaultBounds = new google.maps.LatLngBounds(
      // eslint-disable-next-line no-undef
      new google.maps.LatLng(57.0, 4.0),
      // eslint-disable-next-line no-undef
      new google.maps.LatLng(71.0, 32.0)
    );

    // eslint-disable-next-line no-undef
    const autocompleteOptions: google.maps.places.AutocompleteOptions = {
      bounds: defaultBounds,
      componentRestrictions: { country: "no" },
      fields: ["geometry"],
      strictBounds: false,
    };
    // eslint-disable-next-line no-undef
    autocomplete = new google.maps.places.Autocomplete(inputField, autocompleteOptions);

    autocomplete.setBounds(
      // eslint-disable-next-line no-undef
      new google.maps.LatLngBounds(
        // eslint-disable-next-line no-undef
        new google.maps.LatLng(57.0, 4.0),
        // eslint-disable-next-line no-undef
        new google.maps.LatLng(71.0, 32.0)
      )
    );
    map.addListener("bounds_changed", () => {
      // eslint-disable-next-line no-undef
      autocomplete.setBounds(map.getBounds() as google.maps.LatLngBounds);
    });

    autocomplete.addListener("place_changed", () => {
      // eslint-disable-next-line no-undef
      const places = autocomplete.getPlace() as google.maps.places.PlaceResult;

      // eslint-disable-next-line no-undef
      const bounds = new google.maps.LatLngBounds();
      if (!places.geometry || !places.geometry.location) {
        return;
      }
      if (places.geometry.viewport) {
        bounds.union(places.geometry.viewport);
      } else {
        bounds.extend(places.geometry.location);
      }

      map.setZoom(15);
      map.fitBounds(bounds);

      // Only get installators within 100km in air line from search result
      // This is done to prevent to many results when calculating driving distance

      const closestInstallators = current_installator.map((installator: Installator) => {
        const lat = installator.lat;
        const lng = installator.lng;

        // eslint-disable-next-line no-undef
        const installatorLocation = new google.maps.LatLng(lat, lng);

        // eslint-disable-next-line no-undef
        let distance = google.maps.geometry.spherical.computeDistanceBetween(
          // eslint-disable-next-line no-undef
          places.geometry?.location as google.maps.LatLng,
          installatorLocation
        );

        // Only return installators within 100km in air line
        if (distance > 100000 && !Number.isNaN(distance)) return;

        // convert distance to km
        distance = Math.round(distance / 1000);

        return { ...installator, distance, selected: false };
      }) as (Installator & { distance: number } & { selected: false })[];

      // Remove undefined values
      validInstallators = closestInstallators.filter(
        (installator) => installator && installator.distance >= 0
      );

      validInstallators.sort((a, b) => a.distance - b.distance);

      // Only get the 10 closest installators
      validInstallators = validInstallators.slice(0, 10);

      const distanceRequest = {
        origins: [places.geometry.location],
        destinations: validInstallators.map((installator) => {
          const lat = installator.lat;
          const lng = installator.lng;
          // eslint-disable-next-line no-undef
          return new google.maps.LatLng(lat, lng);
        }),
        // eslint-disable-next-line no-undef
        travelMode: google.maps.TravelMode.DRIVING,
        // eslint-disable-next-line no-undef
        unitSystem: google.maps.UnitSystem.METRIC,
      };

      service.getDistanceMatrix(distanceRequest).then((response) => {
        let distance = response.rows[0].elements.map((element) => element.distance.value);

        distance = distance.map((distance) => Math.round(distance / 1000));

        let installators = validInstallators.map((installator, index) => {
          return { ...installator, distance: distance[index] };
        });

        installators.sort((a, b) => a.distance - b.distance);
        validInstallators = installators.filter((installator) => installator.distance < 100);

        if (greenMarker) greenMarker.setMap(null);

        // Add search result as marker as green marker
        // eslint-disable-next-line no-undef
        greenMarker = new google.maps.Marker({
          position: places.geometry?.location,
          map,
          title: places.name,
          icon: {
            url: greenDot,
          },
        });

        // Add all valid installators to map
        markers = validInstallators.map((installator) => {
          const lat = installator.lat;
          const lng = installator.lng;
          // eslint-disable-next-line no-undef
          const installatorLocation = new google.maps.LatLng(lat, lng);
          // eslint-disable-next-line no-undef
          return new google.maps.Marker({
            position: installatorLocation,
            map,
            title: installator.title,
            optimized: false,
            icon: {
              url: yellowDot,
            },
          });
        });

        markers.map((marker) => {
          marker.addListener("click", () => {
            validInstallators = validInstallators.map((installator) => {
              if (installator.title === (marker.getTitle() as string)) {
                return { ...installator, selected: true };
              }
              return { ...installator, selected: false };
            }) as (Installator & { distance: number } & { selected: boolean })[];
          });
        });

        // Zoom in on clicked marker
        markers.map((marker) => {
          marker.addListener("click", () => {
            map.setZoom(10);
            // eslint-disable-next-line no-undef
            map.setCenter(marker.getPosition() as google.maps.LatLng);
            marker.setIcon({ url: redDot });
          });
        });
      });
    });
  });
</script>

<div class="search-results">
  <div class="search-input">
    <p class="font-bold">Velg installatør</p>
    <label class="pb-2" for="installator-search">Søk etter installatør</label>
    <input
      id="installator-search"
      class="search"
      type="text"
      bind:this={inputField}
      placeholder="Søk på adresse / sted / postnr ..."
    />
    <div class="search-result__container">
      <p class="sr-only" aria-live="polite">
        Antall intallatører for valgt område {validInstallators.length}
        Nærmeste installatør er {validInstallators[0]?.title} og er {validInstallators[0]?.distance}
        kilometer fra ditt valgte sted
      </p>
      {#each validInstallators as installator (installator)}
        <button
          class="search-result button px-4"
          class:selected-search={installator.selected}
          onclick={() => selectInstallator(installator)}
        >
          <h3 class="mb-0">{installator.title}</h3>
          <p class="mt-0">{installator.distance} km</p>
        </button>
      {/each}
    </div>

    {#if validInstallators.some((installator) => installator.selected && installator.distance > 50)}
      <InfoBanner type="warning">
        Valgt installatør ligger utenfor 50km tur/retur. Pris for kjøring utover dette avtaler du
        direkte med installatør når de kontakter deg.
      </InfoBanner>
    {/if}
    {#if validInstallators.some((installator) => installator.selected)}
      <TypeformButton
        class="button-primary mt-4 underline w-fit mx-auto"
        url={`//ishavskraft.typeform.com/to/${typeform}`}
        {source}
        customParameters={{
          ...(product && { [productKey]: product || "" }),
          ...(productId && { product_id: productId }),
          ...(campaign && { campaign: campaign }),
          ...(selected_installer_id && { installer_id: selected_installer_id }),
          ...(selected_installer && { installer: selected_installer }),
        }}
      >
        Bekreft valgt installatør og bestill
      </TypeformButton>
    {/if}
  </div>
  <div class="map mt-4" bind:this={container}></div>
</div>

<style>
  .map {
    height: 600px;
  }
  .search {
    height: 2.5em;
    border-radius: 1rem;
    border: 1px solid var(--primary-dark);
    padding-inline-start: var(--s-4);
  }

  .search-results {
    display: flex;
    flex-direction: column;
  }

  .search-input {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  .search-result__container {
    margin-top: var(--s-4);
    max-height: 350px;
    overflow-y: auto;
  }

  .search-result {
    border-radius: 1rem;
    border: 1px solid var(--primary-dark);
    margin-block-start: 1rem;
    text-align: center;
    width: 100%;
  }

  .selected-search {
    background-color: var(--primary-dark);
    color: white;
  }
  @media (--tablet) {
    .search-results {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
    }
    .search-input {
      padding-inline-end: var(--s-4);
    }
    .search-result__container {
      max-height: 500px;
    }
  }
</style>
